
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Slide,
    LinearProgress,
    Button
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses'
import CloseIcon from "@mui/icons-material/Close";
import {useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {ObjectModel,
        ObjectFactory,
        RecordJSON,
        RecordModel,
        RecordFactory,
        ListViewModel,
        ListViewFilterModel,
        ListViewFactory,
        ListViewColumnModel,
        LayoutFieldModel} from '../../models/models';
import {ListConfigurationModel,
        ListConfigurationJSON,
        ListConfigurationFactory,
        ListViewConfigurationModel,
        ListViewConfigurationJSON,
        ListViewConfigurationFactory} from  '../../models/modelsListConfiguration';
import {SortOrderModel,
        SortOrderFactory,
        BarChartUIPreferenceModel} from '../../models/modelsUserConfiguration';
import {ErrorComponent} from '../error/Error';
import Config from "../../config";
import MonitorIcon from '@mui/icons-material/Monitor';
import FilterListIcon from '@mui/icons-material/FilterList';
import {ListSelectorComponent} from '../listSelector/ListSelector';
import {ListViewComponent} from '../list/ListView';
import {ListFilterComponent} from '../list/ListFilter';
import { useAuth } from "../../hooks/useAuth";
import {useSnackbar} from "notistack";

/*

COMPONENT FOR DISPLAYING DIALOG WHEN RECORD SHALL BE DELETED

*/

interface RequestBody {
  crm_list_config: ListConfigurationJSON | null,
  crm_listview_config: ListViewConfigurationJSON | null
}

export const LookupDialogComponent: React.FC<{
  objectApiName: string | null,  //this is the object name of the lookup field
  openDialog: boolean,
  onCloseDialog: () => void,
  onSelect: (selectedRecord: RecordModel | null) => void
}> = ({
  objectApiName,
  openDialog,
  onCloseDialog,
  onSelect
}) => {

  const styles = ContainerClasses();

  let {appName} = useParams();
  const {accessToken, onInvalidToken} = useAuth();

  const dispatch = useDispatch<any>();

  const { enqueueSnackbar } = useSnackbar();

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [objectMetadata, setObjectMetadata] = useState<ObjectModel | null>(null);
  const [listViews, setListViews] = useState<ListViewModel[]>([]);
  const [selectedListView, setSelectedListView] = useState<ListViewModel | null>(null);
  const [lookupRecords, setLookupRecords] = useState<RecordModel[]>([]);
  const [listConfig, setListConfig] = useState<ListConfigurationModel>(new ListConfigurationFactory().init())
  const [listViewConfig, setListViewConfig] = useState<ListViewConfigurationModel>(new ListViewConfigurationFactory().init())
  const [sortOrders, setSortOrders] = useState<SortOrderModel[]>([]);
  const [selectedListViewColumns, setSelectedListViewColumns] = useState<ListViewColumnModel[]>([]);

  const [displayFilters, setDisplayFilters] = useState<boolean>(false);
  const [displaySaveFilters, setDisplaySaveFilters] = useState<boolean>(false);

  const retrieveObjectInformation = async (objectApiName: string,
                                           listViewApiName: string | null,
                                           listConfig: ListConfigurationModel | null,
                                           listViewConfig: ListViewConfigurationModel | null,
                                           sortOrders: SortOrderModel[] | null,
                                           authToken: string) => {

    setIsFetching(true);
    setErrorMessage(null);

    try {
      //get object information for the selected lookup
      let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}`;
      const headers = new Headers();

      headers.append("Authorization", `Bearer ${authToken}`);
      headers.append("Accept", "application/json");
      headers.append("Content-Type", "application/json");
      headers.append("crm-listview-api-name", `${listViewApiName}`);
      headers.append("crm-context", "lookup-list");

      const requestBody: RequestBody = {
        crm_list_config: null,
        crm_listview_config: null
      };

      if(listConfig){
        const listConfigJSON = new ListConfigurationFactory().toJSON(listConfig);
        requestBody.crm_list_config = listConfigJSON
      }

      if(listViewConfig){
        const listViewConfigJSON = new ListViewConfigurationFactory().toJSON(listViewConfig);
        requestBody.crm_listview_config = listViewConfigJSON
      }

      if(sortOrders){
        const sortOrderFactory = new SortOrderFactory();
        const sortOrdersJSON = sortOrders.map((thisSortOrder: SortOrderModel) => sortOrderFactory.toJSON(thisSortOrder));
        headers.append("crm-sort-orders", JSON.stringify(sortOrdersJSON));
      }

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          setErrorMessage("Error when fetching records");
        } else{
          setErrorMessage(errorData.message);
        }
        return;
      }

      const data = await response.json();

      if(!data ||
         !data.listview ||
         !data.listviews ||
         !Array.isArray(data.listviews) ||
         !data.records ||
         !Array.isArray(data.records) ||
         !data.metadata ||
         !data.metadata.object ||
         !data.list_config ||
         !data.listview_config){
          setErrorMessage("Error when fetching data");
          return;
      }

      setObjectMetadata(new ObjectFactory().fromJSON(data.metadata.object))

      setListViews(data.listviews.map((listview: any) => new ListViewFactory().fromJSON(listview)))

      const thisListView = new ListViewFactory().fromJSON(data.listview)
      setSelectedListView(thisListView);

      if(thisListView !== null){
        const listViewColumns = thisListView.listview_columns.map((column) => column)
        setSelectedListViewColumns(listViewColumns);
      }

      setLookupRecords(data.records.map((record: any) => new RecordFactory().fromJSON(record)))

      const thisListConfig = new ListConfigurationFactory().fromJSON(data.list_config);
      setListConfig(thisListConfig)

      const thisListViewConfig = new ListViewConfigurationFactory().fromJSON(data.listview_config);
      thisListViewConfig.paginator.loadedPages.push(thisListViewConfig.paginator.currentPage)
      setListViewConfig(thisListViewConfig)

      //prefetch records
      prefetchRecords(
          objectApiName,
          thisListView.ApiName_sys,
          thisListConfig,
          thisListViewConfig,
          sortOrders,
          authToken
      )

      setIsFetching(false);
    } catch(err) {
      setErrorMessage(`Unhandled Error in retrieve_object_information: ${err}`);
    }

  }

  const prefetchRecords = async (objectApiName: string,
                                 listViewApiName: string | null | undefined,
                                 listConfig: ListConfigurationModel,
                                 listViewConfig: ListViewConfigurationModel,
                                 sortOrders: SortOrderModel[] | null,
                                 authToken: string) => {
    //prefetch next page
    const thisPaginator = listViewConfig.paginator;
    const pageNumber = thisPaginator.nextPage;

    if(thisPaginator.currentPage === pageNumber){
      //nothing to be done, because end of list already found
      return;
    }
    if(thisPaginator.loadedPages.includes(pageNumber)){
      //nothing to be done, because page already loaded
      return;
    }
    if(thisPaginator.loadingPages.includes(pageNumber)){
      //nothing to be done, because page is currently already being loaded
      return;
    }
    if(!listViewApiName){
      //if there is no listview, nothing to be loaded
      return;
    }

    const newListViewConfig = {...listViewConfig,
                               paginator: {...listViewConfig.paginator,
                                           currentPage: pageNumber}}

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}`;
    const headers = new Headers();

    const thisListConfigJSON = new ListConfigurationFactory().toJSON(listConfig)
    const thisListViewConfigJSON = new ListViewConfigurationFactory().toJSON(newListViewConfig)

    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");
    headers.append("crm-listview-api-name", `${listViewApiName}`);
    headers.append("crm-context", "lookup-list");
    if(sortOrders){
      const sortOrderFactory = new SortOrderFactory();
      const sortOrdersJSON = sortOrders.map((thisSortOrder: SortOrderModel) => sortOrderFactory.toJSON(thisSortOrder));
      headers.append("crm-sort-orders", JSON.stringify(sortOrdersJSON));
    }

    const requestBody: RequestBody = {
      crm_list_config: thisListConfigJSON,
      crm_listview_config: thisListViewConfigJSON
    };



    try {
      //set loading page number
      setListViewConfig(prev => ({...prev,
                                 paginator: {...prev.paginator,
                                             loadingPages: [...prev.paginator.loadingPages, pageNumber]}
                                  }));


      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(!response.ok) {
        const errorData = await response.json();
        console.log('Error in prefetch', errorData)
        return;
      }

      const data = await response.json();

      if(!data ||
         !data.records ||
         !Array.isArray(data.records) ||
         !data.listview_config ||
         !data.list_config){
        console.log('Error in prefetch')
        return;
      }

      const thisListConfig = new ListConfigurationFactory().fromJSON(data.list_config);
      setListConfig(thisListConfig)

      const additionalRecords = data.records.map((record: RecordJSON) => new RecordFactory().fromJSON(record))
//      let allRecords = [...lookupRecords]
//      allRecords = allRecords.concat(additionalRecords)
      setLookupRecords(prev => [...prev].concat(additionalRecords));

      //store actually loaded page from listview_config
      const thisListViewConfig = new ListViewConfigurationFactory().fromJSON(data.listview_config);
      const loadedPageNumber = thisListViewConfig.paginator.currentPage
      //ONLY adapt loadedPages
      setListViewConfig(prev => ({...prev,
                                 paginator: {...prev.paginator,
                                             loadedPages: [...prev.paginator.loadedPages, loadedPageNumber],
                                             loadingPages: prev.paginator.loadingPages.filter((thisPage) => thisPage != loadedPageNumber)}
                                  }));
    } catch(err) {
      //remove pageNumber from loading pages;
      setListViewConfig(prev => ({...prev,
                                 paginator: {...prev.paginator,
                                             loadingPages: prev.paginator.loadingPages.filter((thisPage) => thisPage != pageNumber)}
                                  }));
      console.log(`Prefetch Error: ${err}`)
    }

  }

  const applyFilters = (filters: ListViewFilterModel[], filterLogic: string)  => {

    const errors = filters.map((thisFilter: ListViewFilterModel) => {
      if(thisFilter.Value_sys === null || thisFilter.Value_sys === undefined) return `Error on ${thisFilter.ReferencedField_sys.Label_sys}: null/undefined value not allowed`
      return ''
    }).filter((errorMessage: string) => errorMessage !== '')

    if(errors.length > 0){
      enqueueSnackbar(errors.join('; '), {
        variant: "error",
      });
      return;
    }
    //now, send filters to backend
    if(objectApiName && selectedListView){
      const newListConfig = {...listConfig,
                             applyCustomFilters: true,
                             filterLogic: filterLogic,
                             filters: filters}
      //reset ordering (saved in backend anyways)
      //reset paginator and search
      const thisSortOrders = null;
      const thisListViewConfig = null;
      retrieveObjectInformation(objectApiName, selectedListView.ApiName_sys, newListConfig, thisListViewConfig, thisSortOrders, accessToken);
    }
    setDisplayFilters(false)
  }

  const saveFilters = (overwriteListView: boolean, listViewLabel: string, filters: ListViewFilterModel[], filterLogic: string) => {
    //do nothing
  }

  const resetFilters = ()  => {
    if(objectApiName && selectedListView){
      const newListConfig = {...listConfig,
                             filter_logic: selectedListView.FilterLogic_sys,
                             filters: selectedListView.listview_filters.map((thisFilter: ListViewFilterModel)=>thisFilter)
                           }
      //reset ordering (saved in backend anyways)
      //reset paginator and search
      const thisSortOrders = null;
      const thisListViewConfig = null;
      retrieveObjectInformation(objectApiName, selectedListView.ApiName_sys, newListConfig, thisListViewConfig, thisSortOrders, accessToken);
    }
    setDisplayFilters(false)
  }


  useEffect(() => {

    if(openDialog===false){
      //reset listview configuration
      setListConfig(new ListConfigurationFactory().init())
      setListViewConfig(new ListViewConfigurationFactory().init())
    }

    if(openDialog===true && objectApiName){
      const thisSortOrders = null;
      retrieveObjectInformation(objectApiName, null, listConfig, listViewConfig, thisSortOrders, accessToken)
    }

}, [openDialog]); // eslint-disable-line react-hooks/exhaustive-deps



  const selectListView = (thisListView: ListViewModel) => {
    //remove sort orders
    const thisSortOrders = null;
    //reset paginator
    const thisListViewConfig = {...listViewConfig,
                                paginator: {...listViewConfig.paginator,
                                            currentPage: 0,
                                            loadedPages: [],
                                            loadingPages: []}}
    //disable custom filters
    const thisListConfig = {
      ...listConfig,
      applyCustomFilters: false
    }
    if(objectApiName) retrieveObjectInformation(objectApiName, thisListView.ApiName_sys, thisListConfig, thisListViewConfig, thisSortOrders, accessToken);
  }

  const switchPaginatorPage = (listViewConfig: ListViewConfigurationModel, newPage: number) => {
    const listViewApiName = selectedListView?.ApiName_sys;
    if(!objectApiName) return;
    //set the new paginator when switching a page
    const pageCount = Math.ceil(listViewConfig.paginator.recordCount/listViewConfig.paginator.pageSize)
    const nextPage = Math.min(newPage + 1, pageCount-1); //use pageCount-1 because pageNumber is actually the index
    const previousPage = Math.max(0,newPage - 1);

    const newListViewConfig = {...listViewConfig,
                               paginator: {...listViewConfig.paginator,
                                           currentPage: newPage,
                                           previousPage: previousPage,
                                           nextPage: nextPage}}
    setListViewConfig(newListViewConfig);

    //and prefetch records
    prefetchRecords(
          objectApiName,
          listViewApiName,
          listConfig,
          newListViewConfig,
          sortOrders,
          accessToken
    )
  }

  const handleSort = (listViewConfig: ListViewConfigurationModel, sortOrders: SortOrderModel[]) => {
    setSortOrders(sortOrders);

    if(selectedListView && objectApiName){
      //reset paginator when changing ordering
      const thisListViewConfig = {...listViewConfig,
                                  paginator: {...listViewConfig.paginator,
                                              currentPage: 0}}
      retrieveObjectInformation(objectApiName, selectedListView.ApiName_sys, listConfig, thisListViewConfig, sortOrders, accessToken);
    }
  }

  const handleFrontendSearch = (listViewConfig: ListViewConfigurationModel) => {
    setListViewConfig(listViewConfig)
  }

  const handleDatabaseSearch = (listViewConfig: ListViewConfigurationModel) => {

    setListViewConfig(listViewConfig);

    if(selectedListView && objectApiName){
      retrieveObjectInformation(objectApiName, selectedListView.ApiName_sys, listConfig, listViewConfig, sortOrders, accessToken);
    } else if(objectApiName) {
      retrieveObjectInformation(objectApiName, null, listConfig, listViewConfig, null, accessToken);
    }
  }

  const setBarChartConfig = (listView: ListViewModel, barChartGroups: BarChartUIPreferenceModel[]) => {
    enqueueSnackbar('BarChart in Lookup not implemented yet', {
      variant: "error",
    });
  }



  const selectRecord = (recordUuid: string) => {

    const filteredRecords = lookupRecords.filter((thisRecord) => {

            const recordFields = thisRecord.fields;
            if(recordFields.Uuid_sys.fieldValue === recordUuid){
              return 1
            }
            return 0
          })

    let selectedRecord = null
    if(filteredRecords.length > 0){
      selectedRecord = filteredRecords[0];
    }

    onSelect(selectedRecord)

  }


  return (

    <Dialog
          open={openDialog}
          onClose={onCloseDialog}
          fullWidth
          maxWidth={"lg"}
          TransitionComponent={Slide}
          TransitionProps={{direction: "up"} as any}
    >


       <ListFilterComponent
         displayFilters={displayFilters}
         listConfig={listConfig}
         onCloseDialog={() => {setDisplayFilters(false)}}
         onClearDialog={resetFilters}
         onApplyFilters={applyFilters}
         context={{objectApiName: objectApiName}}
       />

       <Box sx={styles.containerRowB}>
          <Box></Box>

          <IconButton
             edge="start"
             color="inherit"
             aria-label="close"
             onClick={onCloseDialog}
          >
             <CloseIcon />
          </IconButton>
       </Box>

       <DialogTitle>
          {isFetching && <LinearProgress color="primary" />}
          {!isFetching &&
              <Box sx={styles.containerRowB}>
                 <Box sx={styles.containerColB}>
                    <MonitorIcon />
                    <Typography variant="subtitle2">
                       {objectMetadata?.PluralLabel_sys}
                    </Typography>
                 </Box>
              <Box></Box>
              </Box>
          }
       </DialogTitle>

       {!isFetching &&
          <DialogContent
            sx={{
              display:'flex',
              flexDirection:'column',
              gap: 2
            }}
          >
            <Box sx={{flex: 1,
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      alignItems: "center"}}
            >

              <Box
                sx={{display: "flex",
                     flexDirection: "column",
                     alignItems: "center",
                     gap: 2
                   }}
              >
                <ListSelectorComponent
                  selectedList = {selectedListView}
                  availableLists = {listViews}
                  selectList = {selectListView}
                />

                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  onClick={() => {setDisplayFilters(!displayFilters)}}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%"
                    }}
                  >
                    <Typography>
                      Customize Filters
                    </Typography>
                    <FilterListIcon fontSize="small"/>
                  </Box>
                </Button>
              </Box>
            </Box>

            {selectedListView &&
              <ListViewComponent
              listView={selectedListView}
              records={lookupRecords}
              listConfig={listConfig}
              listViewConfig={listViewConfig}
              switchPaginatorPage={switchPaginatorPage}
              loadSortedRecords={handleSort}
              onDatabaseSearch={handleDatabaseSearch}
              onFrontendSearch={handleFrontendSearch}
              onSave = {()=>{}}
              selectRecord={selectRecord}
              context={{lookupObjectName: objectApiName}}
          />
        }

          </DialogContent>
       }

       {errorMessage !== null &&
           <ErrorComponent
              errorMessage = {errorMessage}
           />
       }


      </Dialog>


  );

};
